'use client';

import styles from './StreamsRangeInput.module.scss';

import React from 'react';

type Props = {
  min?: number;
  max?: number;
  step?: number;
  value?: number;
  defaultValue?: number;
  onChange?: (val: number) => void;
  costPerUnit?: number;
  formatCost?: (n: number) => string;
  leftLabel?: string;
  rightLabel?: string;
  className?: string;
  dictionary?: {
    leftLabel: string;
    rightLabel: string;
  };
};

export default function StreamsRangeInput({
  min = 0,
  max = 2560,
  step = 40,
  value,
  defaultValue = 1280,
  onChange,
  costPerUnit = 0.1,
  formatCost = (n) => `$${Math.round(n * 100) / 100}`,
  leftLabel,
  rightLabel,
  className = '',
  dictionary,
}: Props) {
  const isControlled = value !== undefined;
  const [inner, setInner] = React.useState<number>(defaultValue);
  const val = isControlled ? (value as number) : inner;

  const finalLeftLabel = dictionary?.leftLabel || leftLabel || 'streams';
  const finalRightLabel = dictionary?.rightLabel || rightLabel || 'costs';

  const percent = React.useMemo(() => {
    const rng = max - min;
    if (rng <= 0) return 0;
    return ((val - min) / rng) * 100;
  }, [val, min, max]);

  const id = React.useId();

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = Number(e.target.value);
    if (!isControlled) setInner(v);
    onChange?.(v);
  };

  const cost = React.useMemo(() => val * costPerUnit, [val, costPerUnit]);

  return (
    <div className={`${styles.wrap} ${className}`}>
      <div className={styles.hud}>
        <div className={styles.stat}>
          <div aria-live="polite">{`${val}K`}</div>
          <small>{finalLeftLabel}</small>
        </div>

        <div className={styles.stat + ' ' + styles.right}>
          <div aria-live="polite">{formatCost(cost)}</div>
          <small>{finalRightLabel}</small>
        </div>
      </div>

      <div
        className={styles.slider}
        style={{ ['--val']: `${percent}%` } as React.CSSProperties}
        data-min={min}
        data-max={max}
        data-step={step}
      >
        <div className={styles.bar} />
        <input
          id={id}
          type="range"
          min={min}
          max={max}
          step={step}
          value={val}
          onChange={handleInput}
          aria-label={finalLeftLabel}
        />
      </div>
    </div>
  );
}
